import React, { useCallback, useMemo } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { useData } from '../../data/DataProvider';
import { useToast } from '../../shared/components/ToastProvider';
import { statusColor } from '../../shared/statusColors';

// Naive-local formatter — MUST use local getters, never toISOString()
// (that converts to UTC and shifts the displayed time).
function toNaiveLocal(date) {
  if (!date) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:00`;
}

// FullCalendar drag-and-drop schedule (docs/pass4_dev_brief.md §3).
//   drag   → reschedules the job (scheduled_start/end shift together)
//   resize → changes the job's duration (scheduled_end)
//   click  → opens the shared JobEditModal IN PLACE (no client-page detour)
// Events are colour-coded by job status; filtering is done upstream
// (Schedule.jsx passes the filtered jobs).
export default function ScheduleCalendar({ jobs, clientName, onEditJob }) {
  const { update, patchLocal } = useData();
  const { notify } = useToast();

  const events = useMemo(() => jobs.map((job) => {
    const colors = statusColor(job.status);
    return {
      id: job.id,
      title: `${job.title}${clientName(job.client_id) ? ` — ${clientName(job.client_id)}` : ''}`,
      start: job.scheduled_start || job.date || undefined,
      end: job.scheduled_end || undefined,
      backgroundColor: colors.bg,
      borderColor: colors.border,
      textColor: colors.text,
      // Pass 6: client-requested jobs get a dashed border (reschedule) or
      // the requested-blue tone; the badge/tooltip live in CSS/eventDidMount.
      classNames: job.status === 'reschedule_requested' ? ['event-reschedule'] : job.status === 'requested' ? ['event-requested'] : [],
      extendedProps: { job },
    };
  }), [jobs, clientName]);

  // Tooltip for reschedule requests — the client's preferred date + note.
  const handleEventDidMount = useCallback((info) => {
    const job = info.event.extendedProps?.job;
    if (!job || job.status !== 'reschedule_requested') return;
    const parts = [];
    if (job.client_requested_date) parts.push(`prefers ${String(job.client_requested_date).slice(0, 10)}`);
    if (job.client_request_note) parts.push(job.client_request_note);
    if (parts.length) info.el.title = `Reschedule requested — ${parts.join(' · ')}`;
  }, []);

  // Accept flow (pass 6 §6C): dragging a requested/reschedule_requested
  // job to a real slot CONFIRMS the request — status flips to scheduled
  // and the client's request metadata is cleared.
  const confirmRequest = (job, patch) => {
    if (job.status !== 'requested' && job.status !== 'reschedule_requested') return patch;
    if (!patch.scheduled_start) return patch;
    return {
      ...patch,
      status: 'scheduled',
      client_requested_date: null,
      client_request_note: null,
    };
  };

  // Persist a schedule change; optimistic update with rollback on failure.
  const persistSchedule = useCallback(async (job, patch) => {
    const previous = job;
    patchLocal('jobs', job.id, patch);
    try {
      const updated = await update('jobs', job.id, { ...job, ...patch });
      return updated;
    } catch (err) {
      patchLocal('jobs', job.id, previous); // rollback
      notify(err.message || 'Could not update the schedule', 'error');
      return null;
    }
  }, [update, patchLocal, notify]);

  const handleEventDrop = useCallback((info) => {
    const job = info.event.extendedProps.job;
    if (!job) return;
    const start = toNaiveLocal(info.event.start);
    // Keep the duration constant; only shift the window.
    const originalDuration = (job.scheduled_end ? new Date(job.scheduled_end) : new Date(job.scheduled_start))
      - new Date(job.scheduled_start);
    const end = toNaiveLocal(new Date(new Date(start).getTime() + originalDuration));
    persistSchedule(job, confirmRequest(job, { scheduled_start: start, scheduled_end: end }));
  }, [persistSchedule, confirmRequest]);

  const handleEventResize = useCallback((info) => {
    const job = info.event.extendedProps.job;
    if (!job) return;
    persistSchedule(job, confirmRequest(job, {
      scheduled_start: toNaiveLocal(info.event.start),
      scheduled_end: toNaiveLocal(info.event.end),
    }));
  }, [persistSchedule, confirmRequest]);

  const handleEventClick = useCallback((info) => {
    const job = info.event.extendedProps.job;
    if (job) onEditJob?.(job);
  }, [onEditJob]);

  return (
    <div className="schedule-calendar">
      <FullCalendar
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        initialView="dayGridMonth"
        headerToolbar={{
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridMonth,timeGridWeek,timeGridDay',
        }}
        events={events}
        editable
        eventResizableFromStart
        eventDrop={handleEventDrop}
        eventResize={handleEventResize}
        eventClick={handleEventClick}
        eventDidMount={handleEventDidMount}
        dayMaxEvents={3}
        height="auto"
        slotMinTime="08:00:00"
        slotMaxTime="19:00:00"
        allDayText="All day"
        eventTimeFormat={{ hour: 'numeric', minute: '2-digit', meridiem: 'short' }}
      />
      <p className="field-hint">Drag an event to reschedule · drag its edge to change duration · click to edit in place.</p>
    </div>
  );
}
